import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Grid,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  IconButton,
  useMediaQuery,
  createTheme,
  ThemeProvider,
  CssBaseline,
  Switch,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import api from '../services/api';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
// Importação de ícones para as features
import SettingsIcon from '@mui/icons-material/Settings';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import UpdateIcon from '@mui/icons-material/Update';
import TouchAppIcon from '@mui/icons-material/TouchApp';
// Novos ícones para as características do sistema
import AssignmentIcon from '@mui/icons-material/Assignment';
import BarChartIcon from '@mui/icons-material/BarChart';
import PeopleIcon from '@mui/icons-material/People';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LockIcon from '@mui/icons-material/Lock';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import PersonIcon from '@mui/icons-material/Person';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import LoginIcon from '@mui/icons-material/Login';

// Função para verificar se há JWT no localStorage
const checkAuth = () => {
  return localStorage.getItem('token') !== null;
};

function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode === 'true';
  });
  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();
  
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  
  useEffect(() => {
    // Se não existe preferência salva, use a preferência do sistema
    if (localStorage.getItem('darkMode') === null) {
      setDarkMode(prefersDarkMode);
    }
  }, [prefersDarkMode]);
  
  // Salvar preferência no localStorage
  useEffect(() => {
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  useEffect(() => {
    // Verificar se o usuário já está autenticado
    if (checkAuth()) {
      setIsAuthenticated(true);
      // Redirecionar para a página de projetos
      setTimeout(() => navigate('/projects'), 1500);
    }
  }, [navigate]);

  // Criar tema com base no modo atual
  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? 'dark' : 'light',
          primary: {
            main: '#1976d2',
          },
          secondary: {
            main: '#dc004e',
          },
          background: {
            default: darkMode ? '#121212' : '#f5f5f5',
            paper: darkMode ? '#1e1e1e' : '#ffffff',
          },
        },
        typography: {
          fontFamily: 'Roboto, Arial, sans-serif',
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                borderRadius: 8,
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                borderRadius: 12,
              },
            },
          },
        },
      }),
    [darkMode]
  );

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };
  
  const handleToggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Preparar os dados para o formato que o FastAPI espera para o login
      const formData = new URLSearchParams();
      formData.append('username', username);
      formData.append('password', password);

      // Fazer a requisição de login usando o cliente API personalizado
      // A URL do token é um endpoint especial que requer headers específicos
      const response = await api.post('/token', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });

      // Armazenar o token no localStorage
      localStorage.setItem('token', response.data.access_token);

      // Obter os dados do usuário para verificar o papel
      const userResponse = await api.get('/users/me');

      // Armazenar informações do usuário localmente
      localStorage.setItem('user_role', userResponse.data.role_id);
      localStorage.setItem('username', userResponse.data.username);

      // Atualizar estado e redirecionar
      setIsAuthenticated(true);
      setLoading(false);

      // Redirecionar para a página principal de projetos
      navigate('/projects');
      
    } catch (error) {
      setLoading(false);
      if (error.response && error.response.status === 401) {
        setError('Nome de usuário ou senha incorretos.');
      } else {
        setError('Erro ao fazer login. Por favor, tente novamente mais tarde.');
        console.error('Erro de login:', error);
      }
    }
  };

  // Componente de recurso reutilizável
  const FeatureBox = ({ icon, title, description }) => (
    <Paper
      elevation={3}
      sx={{
        mb: 2,
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        backgroundColor: 'background.paper',
        transition: 'transform 0.2s',
        '&:hover': {
          transform: 'translateY(-5px)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        {icon}
        <Typography variant="h6" sx={{ ml: 1, fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Paper>
  );

  // Se o usuário já estiver autenticado
  if (isAuthenticated) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            mt: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper
            elevation={3}
            sx={{ p: 4, width: '100%', textAlign: 'center' }}
          >
            <Typography variant="h5" component="h1" gutterBottom>
              Você já está logado.
            </Typography>
            <Typography variant="body1" gutterBottom>
              Redirecionando para a página de projetos...
            </Typography>
            <CircularProgress sx={{ mt: 2 }} />
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'background.default',
          position: 'relative',
        }}
      >
        {/* Toggle de Modo Escuro/Claro */}
        <Box 
          sx={{ 
            position: 'absolute', 
            top: 20, 
            right: 20, 
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Brightness7Icon sx={{ color: darkMode ? 'rgba(255,255,255,0.5)' : '#f9a825', mr: 1 }} />
          <Switch
            checked={darkMode}
            onChange={handleToggleDarkMode}
            color="primary"
          />
          <Brightness4Icon sx={{ color: darkMode ? '#90caf9' : 'rgba(0,0,0,0.5)', ml: 1 }} />
        </Box>
        
        <Container maxWidth="lg" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
          <Grid container spacing={4} alignItems="center">
            {/* Coluna de recursos (lado esquerdo) */}
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2 }}>
                <FeatureBox
                  icon={<AssignmentIcon color="primary" fontSize="large" />}
                  title="Gerenciamento de Projetos"
                  description="Crie, edite e organize projetos com definição de prioridades e controle de status em uma interface intuitiva."
                />
                <FeatureBox
                  icon={<BarChartIcon color="primary" fontSize="large" />}
                  title="Análise de Viabilidade"
                  description="Formulários detalhados para análise técnica, financeira e estratégica com geração de relatórios e avaliação de riscos."
                />
                <FeatureBox
                  icon={<PeopleIcon color="primary" fontSize="large" />}
                  title="Gestão de Técnicos"
                  description="Cadastro completo de responsáveis técnicos com associação a projetos e vinculação às contas de usuário."
                />
                <FeatureBox
                  icon={<AccessTimeIcon color="primary" fontSize="large" />}
                  title="Controle de Tempo"
                  description="Registro detalhado de horas trabalhadas com sistema de pausas e cálculo automático de tempo por projeto."
                />
                <FeatureBox
                  icon={<LockIcon color="primary" fontSize="large" />}
                  title="Controle de Acesso"
                  description="Perfis de usuário específicos com permissões personalizadas e proteção de rotas para maior segurança."
                />
              </Box>
            </Grid>

            {/* Coluna de login (lado direito) */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={3}
                sx={{
                  p: 4,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  borderRadius: 2,
                  backgroundColor: 'background.paper',
                }}
              >
                {/* Logo */}
                <Box 
                  sx={{ 
                    mb: 5, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center' 
                  }}>
                  <Box 
                    component="img"
                    src="/logo/logo.svg"
                    alt="Cartório Criciúma"
                    sx={{ 
                      width: 280,
                      mb: 4,
                      filter: darkMode ? 'brightness(1.2) contrast(1.2)' : 'none'
                    }}
                  />
                </Box>

                <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Username
                  </Typography>
                  <TextField
                    fullWidth
                    id="username"
                    name="username"
                    autoComplete="username"
                    autoFocus
                    variant="outlined"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    sx={{ 
                      mb: 3, 
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Box 
                            component="div" 
                            sx={{ 
                              width: 16, 
                              height: 16, 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              color: 'text.secondary',
                              cursor: 'pointer'
                            }}
                            onClick={() => setUsername('')}
                          >
                            ×
                          </Box>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Senha
                  </Typography>
                  <TextField
                    fullWidth
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    autoComplete="current-password"
                    variant="outlined"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    sx={{ 
                      mb: 2, 
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleClickShowPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Box sx={{ display: 'flex', mb: 3 }}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          name="rememberMe"
                          color="primary"
                        />
                      }
                      label="Lembrar-me"
                    />
                  </Box>

                  {error && (
                    <Typography color="error" sx={{ mt: 1, mb: 1 }}>
                      {error}
                    </Typography>
                  )}

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{ 
                      mt: 1, 
                      mb: 2, 
                      py: 1.5, 
                      borderRadius: 1.5,
                      fontWeight: 'bold'
                    }}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Entrar'}
                  </Button>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default Login; 